import { Workflow, WorkflowTemplate, ExecuteWorkflowRequest, CreateWorkflowRequest, UpdateWorkflowRequest, GetWorkflowExecutionRequest, GetWorkflowRequest, RetryWorkflowExecutionRequest } from '@amzn/finspace-service-typescript-client';
import { FinSpaceServiceApi } from '../api/finspaceServiceApi';
import { handleApiRequest } from '../api/utils/apiRequest';
import { createStore } from './store-factory';

interface WorkflowState {
  workflows: Workflow[];
  workflowTemplates: WorkflowTemplate[];
  workflowExecution: any;
  isLoading: boolean;
  isTemplatesLoading: boolean;
  isExecutionLoading: boolean;
  isExecuting: boolean;
  isDeleting: boolean;
  isDeletingExecution: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isRetrying: boolean;
  error: any;
  fetchWorkflows: (spaceId: string) => Promise<Workflow[]>;
  getWorkflow: (spaceId: string, workflowId: string) => Promise<Workflow | null>;
  fetchWorkflowTemplates: () => Promise<void>;
  getWorkflowExecution: (request: GetWorkflowExecutionRequest) => Promise<any>;
  deleteWorkflow: (spaceId: string, workflowId: string) => Promise<void>;
  deleteWorkflowExecution: (spaceId: string, workflowId: string, workflowExecutionId: string) => Promise<void>;
  executeWorkflow: (request: ExecuteWorkflowRequest) => Promise<any>;
  retryWorkflowExecution: (spaceId: string, workflowId: string, workflowExecutionId: string) => Promise<any>;
  createWorkflow: (request: CreateWorkflowRequest) => Promise<any>;
  updateWorkflow: (spaceId: string, workflowId: string, request: UpdateWorkflowRequest) => Promise<any>;
  clearError: () => void;
}

export const useWorkflowStore = createStore<WorkflowState>('workflow', (set, get) => ({
  workflows: [],
  workflowTemplates: [],
  workflowExecution: null,
  isLoading: false,
  isTemplatesLoading: false,
  isExecutionLoading: false,
  isExecuting: false,
  isDeleting: false,
  isDeletingExecution: false,
  isCreating: false,
  isUpdating: false,
  isRetrying: false,
  error: null,

  fetchWorkflows: async (spaceId: string) => {
    if (!spaceId) return [];
    
    set({ isLoading: true, error: null });
    try {
      const result = await handleApiRequest(() => FinSpaceServiceApi.getWorkflows({ spaceId }));
      const workflows = result?.workflows || [];
      set({ workflows, isLoading: false });
      return workflows;
    } catch (error) {
      set({ error, isLoading: false });
      return [];
    }
  },

  getWorkflow: async (spaceId: string, workflowId: string) => {
    if (!spaceId || !workflowId) return null;
    
    try {
      const result = await handleApiRequest(() => FinSpaceServiceApi.getWorkflow({ spaceId, workflowId }));
      return result?.workflow || null;
    } catch (error) {
      set({ error });
      return null;
    }
  },

  fetchWorkflowTemplates: async () => {
    set({ isTemplatesLoading: true, error: null });
    try {
      const result = await handleApiRequest(() => FinSpaceServiceApi.getWorkflowTemplates());
      const templates = result?.workflowTemplates || [];
      
      // Update auth store BEFORE updating workflow store
      const { useAuthStore } = await import('./auth-store');
      useAuthStore.getState().refreshAuthorizationFromTemplates(templates);
      
      set({ workflowTemplates: templates, isTemplatesLoading: false });
    } catch (error) {
      set({ error, isTemplatesLoading: false });
    }
  },

  deleteWorkflow: async (spaceId: string, workflowId: string) => {
    set({ isDeleting: true, error: null });
    try {
      await handleApiRequest(() => FinSpaceServiceApi.deleteWorkflow(spaceId, workflowId));
      // Remove from local state
      const { workflows } = get();
      set({ workflows: workflows.filter(w => w.id !== workflowId), isDeleting: false });
    } catch (error) {
      set({ error, isDeleting: false });
    }
  },

  deleteWorkflowExecution: async (spaceId: string, workflowId: string, workflowExecutionId: string) => {
    set({ isDeletingExecution: true, error: null });
    try {
      await handleApiRequest(() => FinSpaceServiceApi.deleteWorkflowExecution(spaceId, workflowId, workflowExecutionId));
      set({ isDeletingExecution: false });
    } catch (error) {
      set({ error, isDeletingExecution: false });
    }
  },

  executeWorkflow: async (request: ExecuteWorkflowRequest) => {
    set({ isExecuting: true, error: null });
    try {
      const result = await handleApiRequest(() => FinSpaceServiceApi.executeWorkflow(request));
      set({ isExecuting: false });
      return result; // Return the response containing workflowExecutionId
    } catch (error) {
      set({ error, isExecuting: false });
    }
  },

  retryWorkflowExecution: async (spaceId: string, workflowId: string, workflowExecutionId: string) => {
    set({ isRetrying: true, error: null });
    try {
      const retryRequest: RetryWorkflowExecutionRequest = {
        spaceId,
        workflowId,
        workflowExecutionId
      };

      const result = await handleApiRequest(() => FinSpaceServiceApi.retryWorkflowExecution(retryRequest));
      
      set({ isRetrying: false });
      return result;
    } catch (error) {
      set({ error, isRetrying: false });
    }
  },

  clearError: () => {
    set({ error: null });
  },

  getWorkflowExecution: async (request: GetWorkflowExecutionRequest) => {
    set({ isExecutionLoading: true, error: null });
    try {
      const result = await handleApiRequest(() => FinSpaceServiceApi.getWorkflowExecution(request));
      set({ workflowExecution: result, isExecutionLoading: false });
      return result;
    } catch (error) {
      set({ error, isExecutionLoading: false });
    }
  },

  createWorkflow: async (request: CreateWorkflowRequest) => {
    set({ isCreating: true, error: null });
    try {
      const result = await handleApiRequest(() => FinSpaceServiceApi.createWorkflow(request));
      set({ isCreating: false });
      return result;
    } catch (error) {
      set({ error, isCreating: false });
    }
  },

  updateWorkflow: async (spaceId: string, workflowId: string, request: UpdateWorkflowRequest) => {
    set({ isUpdating: true, error: null });
    try {
      const result = await handleApiRequest(() => FinSpaceServiceApi.updateWorkflow(spaceId, workflowId, request));
      set({ isUpdating: false });
      return result;
    } catch (error) {
      set({ error, isUpdating: false });
    }
  },
}));
